import { useEffect } from 'react';
import {
  Container, Typography, Box, Grid, Button, Stack,
} from '@mui/material';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import babySitterImg from '../assets/baby.jpg';
import artImg from '../assets/art.jpg';
import lansiaImg from '../assets/lansia.png';
import bedImg from '../assets/bed.jpg';

const VALUE_ITEMS = [
  { icon: TrendingUpIcon, color: '#2563eb', bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.18)', titleKey: 'karir.pengalaman', descKey: 'karir.pengalamanDesc' },
  { icon: SchoolIcon,     color: '#f97316', bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.18)', titleKey: 'karir.pelatihan', descKey: 'karir.pelatihanDesc' },
  { icon: PeopleAltIcon,  color: '#059669', bg: 'rgba(5,150,105,0.08)', border: 'rgba(5,150,105,0.18)', titleKey: 'karir.lingkunganKerja', descKey: 'karir.lingkunganKerjaDesc' },
];

const Karir = () => {
  const { t } = useTranslation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const jobList = [
    { titleKey: 'karir.job.babySitter', descKey: 'karir.job.babySitterDesc', image: babySitterImg, color: '#2563eb' },
    { titleKey: 'karir.job.art',        descKey: 'karir.job.artDesc',        image: artImg,        color: '#f97316' },
    { titleKey: 'karir.job.lansia',     descKey: 'karir.job.lansiaDesc',     image: lansiaImg,     color: '#059669' },
  ];

  const benefits = t('karir.ctaSection.benefits', { returnObjects: true });

  const handleApply = () => {
    const msg = 'Halo CV Mulia Utama Mandiri, saya tertarik untuk mendaftar sebagai tenaga kerja. Saya mendapatkan informasi ini dari website Anda. Mohon informasikan langkah dan persyaratan pendaftarannya. Terima kasih.';
    window.open(`https://wa.me/${t('footer.number')}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', overflow: 'hidden' }}>

      {/* ── HERO ── */}
      <Box sx={{
        position: 'relative',
        overflow: 'hidden',
        background: [
          'radial-gradient(ellipse 65% 80% at 10% 50%, rgba(37,99,235,0.22) 0%, transparent 65%)',
          'radial-gradient(ellipse 50% 60% at 90% 20%, rgba(249,115,22,0.12) 0%, transparent 60%)',
          '#060d1f',
        ].join(', '),
      }}>
        {/* Top accent bar */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #2563eb, #f97316)', zIndex: 2 }} />
        {/* Dot grid */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.065) 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 90% 90% at 30% 50%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 30% 50%, black 30%, transparent 100%)',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 14, md: 18 }, pb: { xs: 10, md: 14 } }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">

            {/* LEFT: content */}
            <Grid item xs={12} md={6}>
              {/* Overline */}
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ width: 24, height: 2.5, bgcolor: '#f97316', borderRadius: 1 }} />
                <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 2.5 }}>
                  {t('karir.hero.title')}
                </Typography>
              </Stack>

              <Typography sx={{
                fontWeight: 900,
                fontSize: { xs: '2.6rem', md: '3.8rem' },
                lineHeight: 1.05, letterSpacing: '-0.04em', color: '#fff', mb: 2.5,
              }}>
                {t('karir.mengapaBergabung').split(' ').slice(0, -1).join(' ')}{' '}
                <Box component="span" sx={{
                  background: 'linear-gradient(90deg, #f97316, #fb923c)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>
                  {t('karir.mengapaBergabung').split(' ').slice(-1)[0]}
                </Box>
              </Typography>

              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.95rem', md: '1.05rem' }, lineHeight: 1.75, mb: 5, maxWidth: 480 }}>
                {t('karir.hero.subtitle1')} {t('karir.hero.subtitle2')}
              </Typography>

              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => document.getElementById('jobs')?.scrollIntoView({ behavior: 'smooth' })}
                sx={{
                  py: 1.7, px: 3.5, borderRadius: 99,
                  bgcolor: '#f97316', color: '#fff',
                  fontSize: '0.92rem', fontWeight: 700, textTransform: 'none',
                  boxShadow: '0 8px 28px -4px rgba(249,115,22,0.55)',
                  '&:hover': { bgcolor: '#ea6c10', transform: 'translateY(-1px)' },
                  transition: 'all 0.2s ease',
                }}
              >
                {t('karir.cta.explore')}
              </Button>
            </Grid>

            {/* RIGHT: image frame — desktop only */}
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ position: 'relative' }}>
                {/* Decorative bg box */}
                <Box sx={{
                  position: 'absolute', bottom: -20, right: -20,
                  width: '65%', height: '65%',
                  bgcolor: 'rgba(249,115,22,0.07)',
                  border: '1px solid rgba(249,115,22,0.18)',
                  borderRadius: '24px', zIndex: 0,
                }} />
                {/* Photo */}
                <Box sx={{
                  position: 'relative', zIndex: 1,
                  borderRadius: '24px', overflow: 'hidden',
                  height: 460,
                  boxShadow: '0 32px 64px -16px rgba(0,0,0,0.5)',
                }}>
                  <Box component="img" src={bedImg} alt="Career" sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
                  <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(6,13,31,0.4) 0%, transparent 60%)' }} />
                </Box>
                {/* Floating stat card */}
                <Box sx={{
                  position: 'absolute', bottom: 32, left: -28, zIndex: 2,
                  bgcolor: '#fff', borderRadius: '16px',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
                  px: 2.5, py: 2,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: 'rgba(5,150,105,0.12)', border: '1px solid rgba(5,150,105,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PeopleAltIcon sx={{ color: '#059669', fontSize: '1.1rem' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', lineHeight: 1, color: '#0f172a' }}>500+</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, mt: 0.3 }}>{t('hero.placements')}</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* ── WHY JOIN US ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        {/* Section header */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Box sx={{ width: 24, height: 2.5, bgcolor: '#2563eb', borderRadius: 1 }} />
            <Typography sx={{ color: '#2563eb', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 2.5 }}>
              CV. Mulia Utama Mandiri
            </Typography>
          </Stack>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            {t('karir.mengapaBergabung')}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {VALUE_ITEMS.map(({ icon: Icon, color, bg, border, titleKey, descKey }, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Box sx={{
                p: { xs: 3.5, md: 4.5 },
                borderRadius: '24px',
                bgcolor: '#fff',
                border: '1px solid #e2e8f0',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: `0 24px 48px -12px ${color}30`,
                  borderColor: border,
                },
              }}>
                <Box sx={{
                  width: 54, height: 54, borderRadius: '14px', mb: 3,
                  bgcolor: bg, border: `1px solid ${border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon sx={{ color, fontSize: '1.5rem' }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', mb: 1.5, letterSpacing: '-0.01em' }}>
                  {t(titleKey)}
                </Typography>
                <Box sx={{ width: 28, height: 3, bgcolor: color, borderRadius: 1, mb: 2 }} />
                <Typography sx={{ color: '#475569', lineHeight: 1.8, fontSize: '0.93rem' }}>
                  {t(descKey)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── AVAILABLE JOBS ── */}
      <Box id="jobs" sx={{ bgcolor: '#0f172a', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>
        {/* Background glow */}
        <Box sx={{ position: 'absolute', top: '-30%', left: '-10%', width: '50%', height: '160%', background: 'radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          {/* Section header */}
          <Box sx={{ mb: { xs: 6, md: 8 } }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Box sx={{ width: 24, height: 2.5, bgcolor: '#f97316', borderRadius: 1 }} />
              <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 2.5 }}>
                {t('karir.pekerjaanTersedia')}
              </Typography>
            </Stack>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
              {t('karir.pekerjaanTersedia')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {jobList.map((job, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Box sx={{
                  borderRadius: '24px', overflow: 'hidden',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  height: '100%',
                  display: 'flex', flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    borderColor: `${job.color}44`,
                    transform: 'translateY(-4px)',
                    boxShadow: `0 20px 48px -12px ${job.color}30`,
                  },
                }}>
                  {/* Cover image */}
                  <Box sx={{ position: 'relative', height: 240, flexShrink: 0 }}>
                    <Box component="img" src={job.image} alt={t(job.titleKey)} sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(6,13,31,0.75) 0%, transparent 55%)' }} />
                    {/* Color top bar */}
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: job.color }} />
                  </Box>

                  {/* Content */}
                  <Box sx={{ p: 3.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff', mb: 1.5, letterSpacing: '-0.01em' }}>
                      {t(job.titleKey)}
                    </Typography>
                    <Box sx={{ width: 28, height: 3, bgcolor: job.color, borderRadius: 1, mb: 2 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.88rem', lineHeight: 1.75, flexGrow: 1 }}>
                      {t(job.descKey)}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleApply}
                      sx={{
                        mt: 3, borderRadius: 99, textTransform: 'none',
                        fontWeight: 700, fontSize: '0.85rem',
                        color: job.color, borderColor: `${job.color}55`,
                        '&:hover': { bgcolor: `${job.color}14`, borderColor: job.color },
                        transition: 'all 0.2s',
                      }}
                    >
                      {t('karir.ctaSection.applyButton')}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA BANNER ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Box sx={{
          position: 'relative',
          borderRadius: '28px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          p: { xs: 4, md: 7 },
        }}>
          {/* Glow decorations */}
          <Box sx={{ position: 'absolute', top: -60, right: -60, width: 240, height: 240, borderRadius: '50%', bgcolor: 'rgba(37,99,235,0.15)', filter: 'blur(60px)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(249,115,22,0.1)', filter: 'blur(50px)', pointerEvents: 'none' }} />

          <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} md={8}>
              <Typography sx={{
                fontWeight: 900, color: '#fff',
                fontSize: { xs: '2rem', md: '2.8rem' },
                lineHeight: 1.1, letterSpacing: '-0.03em', mb: 1,
              }}>
                {t('karir.ctaSection.title').split(' ').slice(0, -1).join(' ')}{' '}
                <Box component="span" sx={{
                  background: 'linear-gradient(90deg, #f97316, #fb923c)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>
                  {t('karir.ctaSection.title').split(' ').slice(-1)[0]}
                </Box>
              </Typography>

              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem', mb: 3.5 }}>
                {t('karir.ctaSection.subtitle')}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                {Object.values(benefits).map((text, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}
                    sx={{ px: 2, py: 0.9, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 15, color: '#4ade80' }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem', fontWeight: 600 }}>{text}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                variant="contained"
                startIcon={<FaWhatsapp size={20} />}
                onClick={handleApply}
                sx={{
                  py: 1.8, px: 4, borderRadius: 99,
                  bgcolor: '#f97316', color: '#fff',
                  fontSize: '0.92rem', fontWeight: 700, textTransform: 'none',
                  boxShadow: '0 12px 32px -6px rgba(249,115,22,0.5)',
                  '&:hover': { bgcolor: '#ea6c10', transform: 'translateY(-2px)' },
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('karir.ctaSection.applyButton')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>

    </Box>
  );
};

export default Karir;
